import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, List, ListItem, ListItemIcon, ListItemText, Typography, TextField, Chip, Alert, CircularProgress, Divider, FormControl, Select, MenuItem, Tooltip } from "@mui/material";
import { CheckCircle, Cancel, Error } from "@mui/icons-material";
 
import { createLoopGoalAction, updateLoopGoalAction } from "../../../../store/actions/loopActions";
import FormattedNumberInput from "../../../CustomComponents/InputFields/FormattedNumberInput";
import { getTotalValueFromSegments, prettifyNumber } from "../../../../functions/Formatters";
import { scoreLevels } from "../../../../context/global";

export default function LoopGoalSetter({ loopId, editable = true }) {
    const dispatch = useDispatch();
    const { loopGoals, loopKPIs, loopThemes, loading, currentLoop } = useSelector((state) => state.loop);
    const { flatKpis, flatThemes } = useSelector((state) => state.themes);
    const { selectedProject } = useSelector((state) => state.projects);

    // Get KPIs for this loop
    const kpis = useMemo(() => loopKPIs.map(lk => flatKpis[lk.kpi]).filter(Boolean), [loopKPIs]);
    
    const [goalValues, setGoalValues] = useState({});
    const [goalDescriptions, setGoalDescriptions] = useState({});
    const [savingStates, setSavingStates] = useState({});

    // Get selected KPIs for this loop
    const selectedKPIs = useMemo(() => {
        return loopKPIs.filter(loopKPI => loopKPI.is_selected).sort((a, b) => flatKpis[a.kpi].name.localeCompare(flatKpis[b.kpi].name));
    }, [loopKPIs]);

    // Group selected KPIs by theme
    const groupedKPIs = useMemo(() => {
        const groups = {};
        
        // Group KPIs by theme
        selectedKPIs.forEach(loopKPI => {
            const kpi = flatKpis[loopKPI.kpi];
            const themeInfo = flatThemes[kpi?.theme];
            if (themeInfo) {
                const themeKey = `${themeInfo.supertheme}-${themeInfo.name}`;
                if (!groups[themeKey]) {
                    groups[themeKey] = {
                        theme: themeInfo,
                        kpis: []
                    };
                }
                groups[themeKey].kpis.push(loopKPI);
            }
        });
        
        // Sort groups by supertheme, then by theme name
        const sortedGroups = Object.values(groups).sort((a, b) => {
            const superthemeComparison = a.theme.supertheme - b.theme.supertheme;
            if (superthemeComparison !== 0) {
                return superthemeComparison;
            }
            return a.theme.name.localeCompare(b.theme.name);
        });
        
        // Sort KPIs within each group
        sortedGroups.forEach(group => {
            group.kpis.sort((a, b) => flatKpis[a.kpi].name.localeCompare(flatKpis[b.kpi].name));
        });
        
        return sortedGroups;
    }, [selectedKPIs, flatKpis, flatThemes]);

    // Get existing goals for this loop
    const existingGoals = useMemo(() => {
        return loopGoals.filter(goal => goal.loop === loopId);
    }, [loopGoals, loopId]);

    // Initialize goal values and descriptions from existing goals
    useEffect(() => {
        const initialValues = {};
        const initialDescriptions = {};
        
        existingGoals.forEach(goal => {
            initialValues[goal.kpi] = goal.value || "";
            initialDescriptions[goal.kpi] = goal.description || "";
        });
        
        setGoalValues(initialValues);
        setGoalDescriptions(initialDescriptions);
    }, [existingGoals]);

    const getKPITypeLabel = useCallback((scaleType) => {
        switch (scaleType) {
            case 0: return "Score";
            case 1: return "Segment";
            case 2: return "Global";
            default: return "Onbekend";
        }
    }, []);

    const getKPITypeColor = useCallback((scaleType) => {
        switch (scaleType) {
            case 0: return "primary";
            case 1: return "secondary";
            case 2: return "success";
            default: return "default";
        }
    }, []);

    const handleGoalValueChange = useCallback((kpiId, value) => {
        setGoalValues(prev => ({
            ...prev,
            [kpiId]: value
        }));
    }, []);

    const handleGoalDescriptionChange = useCallback((kpiId, description) => {
        setGoalDescriptions(prev => ({
            ...prev,
            [kpiId]: description
        }));
    }, []);

    const handleSaveGoal = useCallback(async (kpiId, newValue = undefined) => {
        if (!editable) return;

        setSavingStates(prev => ({ ...prev, [kpiId]: true }));

        try {
            const value = newValue !== undefined ? newValue : goalValues[kpiId];
            const description = goalDescriptions[kpiId];
            // Find the KPI object
            const kpiObj = kpis.find(k => k.id === kpiId);
            // Find the loopTheme whose theme matches the KPI's theme
            const loopThemeForKPI = loopThemes.find(lt => lt.theme === kpiObj.theme);
            const loopThemeId = loopThemeForKPI ? loopThemeForKPI.id : null;
            if (!loopThemeId) {
                throw new Error('Geen bijbehorend aspect gevonden voor deze KPI');
            }

            // Find existing goal for this KPI
            const existingGoal = existingGoals.find(goal => goal.kpi === kpiId);
            const goalData = {
                loop_theme: loopThemeId,
                kpi: kpiId,
                value: value === "" ? null : parseFloat(value),
                description: description || "",
                loop_kpi: kpiObj.id,
            };
            if (existingGoal) await dispatch(updateLoopGoalAction(existingGoal.id, goalData));
            else await dispatch(createLoopGoalAction(goalData));
        } catch (error) {
            console.error("Error saving goal:", error);
        } finally {
            setSavingStates(prev => ({ ...prev, [kpiId]: false }));
        }
    }, [editable, goalValues, goalDescriptions, existingGoals, loopThemes, kpis, dispatch]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    const iconSwitch = useCallback((state) => {
        switch (state) {
            case 'saving': return <CircularProgress size={16} />;
            case 'saved': return <CheckCircle color="success" />;
            case 'changed': return <Error color="default" />;
            case 'error': return <Cancel color="error" />;
            default: return <Cancel color="disabled" />;
        }
    }, []);

    const getTextColor = useCallback((percentage) => {
        switch (percentage) {
            case 0: return "error.main";
            case 1: return "success.main";
            default: return "warning.main";
        }
    }, []);

    const getGoalValueFromPreviousLoop = useCallback((kpiId, currentLoopId=null) => {
        if (!currentLoopId) return null;

        const currentLoopPhaseIndex = selectedProject.phases.findIndex(phase => phase.loops.some(loop => loop.id === currentLoopId));
        const currentLoopIndex = selectedProject.phases[currentLoopPhaseIndex].loops.findIndex(loop => loop.id === currentLoopId);
        let previousLoopIndex = null;
        let previousLoopPhaseIndex = null;
        
        // Weed out the first loop of the first phase (means there is no previous loop)
        if (currentLoopIndex === 0 && currentLoopPhaseIndex === 0) {
            return null;
        }
        if (currentLoopIndex > 0) {
            previousLoopIndex = currentLoopIndex - 1;
            previousLoopPhaseIndex = currentLoopPhaseIndex;
        } else {
            previousLoopPhaseIndex = currentLoopPhaseIndex - 1;
            previousLoopIndex = selectedProject.phases[previousLoopPhaseIndex].loops.length - 1;
        }

        const previousLoop = selectedProject.phases[previousLoopPhaseIndex].loops[previousLoopIndex];
        const previousLoopGoals = previousLoop.loop_kpis.find(lk => lk.kpi === kpiId)?.loop_goals;
        const previousGoalValue = previousLoopGoals?.find(goal => goal.kpi === kpiId)?.value;

        if (previousGoalValue === undefined || previousGoalValue === null) return "Geen doel";
        const kpi = flatKpis[kpiId];
        if (kpi.scale_type !== 0) {
            return `${kpi.maximize ? ">" : "<"} ${prettifyNumber(previousGoalValue, kpi.unit)}`;
        } else {
            return scoreLevels[(previousGoalValue || 0) - 1]?.label;
        }
    }, [selectedProject]);

    const getReferenceValue = useCallback((kpiId, currentLoopId=null) => {
        if (!currentLoopId) return null;

        let currentLoop = null
        selectedProject.phases.forEach(phase => {
            phase.loops.forEach(loop => {
                if (loop.id === currentLoopId) {
                    currentLoop = loop;
                    return;
                }
            });
        });
        if (!currentLoop) return null;
        const referenceDesign = currentLoop.reference_design;
        const referenceDesignValue = referenceDesign?.scores?.find(score => score.kpi === kpiId)?.value;
        if (referenceDesignValue === undefined || referenceDesignValue === null) return "Geen score";

        const kpi = flatKpis[kpiId];
        
        if (kpi.scale_type === 0) {
            return scoreLevels[(referenceDesignValue || 0) - 1]?.label;
        }
        if (kpi.scale_type === 1) {
            return prettifyNumber(getTotalValueFromSegments(referenceDesignValue), kpi.unit);
        }
        if (kpi.scale_type === 2) {
            return prettifyNumber(referenceDesignValue, kpi.unit);
        }
        return null;
    }, [selectedProject]);

    return (
        <Box>
            <Typography variant="h6" component="h2" gutterBottom>
                Doelen
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                Stel doelen in voor de geselecteerde KPI's van deze ontwerpronde.
                Je kunt doelen toevoegen, bewerken en verwijderen.
            </Typography>

            {selectedKPIs.length > 0 && (
                <Box borderRadius={1}>
                    <Typography variant="body2" color={getTextColor(existingGoals.filter(goal => goal.value !== null).length / selectedKPIs.length)}>
                        {existingGoals.filter(goal => goal.value !== null).length} van {selectedKPIs.length} KPI's hebben een doel ingesteld
                    </Typography>
                </Box>
            )}


            {groupedKPIs.length === 0 ? (
                <Alert severity="info">
                    Geen KPI's geselecteerd voor deze ontwerpronde. Selecteer eerst KPI's in de KPI selector.
                </Alert>
            ) : (
                <Box>
                    {groupedKPIs.map((group) => (
                        <Box key={`${group.theme.supertheme}-${group.theme.name}`} sx={{ mb: 3 }}>
                            {/* Theme Header */}
                            <Box sx={{ mb: 1 }}>
                                <Tooltip 
                                    title={group.theme.description || "Geen beschrijving beschikbaar"} 
                                    placement="top"
                                    arrow
                                >
                                    <Typography 
                                        variant="h6" 
                                        component="h3"
                                        sx={{ 
                                            fontWeight: 'bold',
                                            color: 'text.primary',
                                            cursor: 'help',
                                            borderBottom: 2,
                                            borderColor: 'primary.main',
                                            pb: 0.5,
                                            display: 'inline-block'
                                        }}
                                    >
                                        {group.theme.name}
                                    </Typography>
                                </Tooltip>
                            </Box>
                            
                            {/* KPIs under this theme */}
                            <List sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: 1 }}>
                                {group.kpis.map((loopKPI, index) => {
                                    const kpi = kpis.find(k => k.id === loopKPI.kpi);
                                    const goalValue = goalValues[loopKPI.kpi];
                                    const hasGoal = existingGoals.some(goal => goal.kpi === loopKPI.kpi) || (goalValue !== null && goalValue !== undefined && goalValue !== "");
                                    const existingGoal = existingGoals.find(goal => goal.kpi === loopKPI.kpi);
                                    const hasChanged = existingGoal?.value != goalValue;
                                    const isSaving = savingStates[loopKPI.kpi] || false;

                                    const state = isSaving ? "saving" : !hasGoal ? "error" :hasChanged ? "changed" : "saved";
                                    return (
                                        <Box key={loopKPI.kpi}>
                                            <ListItem sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                <Box display="flex" alignItems="center" width="100%">
                                                    <ListItemIcon>
                                                        {iconSwitch(state)}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <Typography variant="body1" fontWeight="medium">
                                                                {kpi?.name || "Onbekende KPI"}
                                                            </Typography>
                                                            {kpi?.unit && (
                                                                <Chip
                                                                    label={kpi.unit}
                                                                    size="small"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                        </Box>
                                                        }
                                                        secondary={
                                                            <Box>
                                                                <Typography gutterBottom variant="body2" color="text.secondary">
                                                                    {kpi?.description || "Geen beschrijving"}
                                                                </Typography>
                                                                {getGoalValueFromPreviousLoop(loopKPI.kpi, currentLoop.id) && (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Doel vorige ronde: {getGoalValueFromPreviousLoop(loopKPI.kpi, currentLoop.id)}
                                                                    </Typography>
                                                                )}
                                                                {getReferenceValue(loopKPI.kpi, currentLoop.id) && (
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        Score referentieontwerp: {getReferenceValue(loopKPI.kpi, currentLoop.id)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        }
                                                    />
                                                </Box>
                                                
                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                    <Box flex={1}>
                                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                                            Doelwaarde
                                                        </Typography>
                                                        {kpi?.qualitative_scale ? (
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    value={goalValues[loopKPI.kpi] || ""}
                                                                    onChange={(e) => {
                                                                        handleGoalValueChange(loopKPI.kpi, e.target.value);
                                                                        handleSaveGoal(loopKPI.kpi, e.target.value);
                                                                    }}
                                                                    disabled={!editable || isSaving}
                                                                    displayEmpty
                                                                >
                                                                    <MenuItem value="">
                                                                        <em>Selecteer een waarde</em>
                                                                    </MenuItem>
                                                                    <MenuItem value={1}>1 - Sterke verslechtering</MenuItem>
                                                                    <MenuItem value={2}>2 - Verslechtering</MenuItem>
                                                                    <MenuItem value={3}>3 - Gelijk</MenuItem>
                                                                    <MenuItem value={4}>4 - Verbetering</MenuItem>
                                                                    <MenuItem value={5}>5 - Sterke verbetering</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        ) : (
                                                            <TextField
                                                                value={goalValues[loopKPI.kpi] || ""}
                                                                onChange={(e) => handleGoalValueChange(loopKPI.kpi, e.target.value)}
                                                                disabled={!editable || isSaving}
                                                                size="small"
                                                                fullWidth
                                                                placeholder={`Voer waarde in${kpi?.unit ? ` in ${kpi.unit}` : ""}`}
                                                                InputProps={{
                                                                    inputComponent: FormattedNumberInput,
                                                                    inputProps: {
                                                                        name: `goal-${loopKPI.kpi}`,
                                                                        unit: kpi?.unit || "",
                                                                    },
                                                                }}
                                                                onBlur={() => hasChanged && handleSaveGoal(loopKPI.kpi)}
                                                            />
                                                        )}
                                                    </Box>
                                                    
                                                    <Box flex={2}>
                                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                                            Beschrijving (optioneel)
                                                        </Typography>
                                                        <TextField
                                                            value={goalDescriptions[loopKPI.kpi] || ""}
                                                            onChange={(e) => handleGoalDescriptionChange(loopKPI.kpi, e.target.value)}
                                                            disabled={!editable || isSaving}
                                                            size="small"
                                                            fullWidth
                                                            placeholder="Beschrijf het doel..."
                                                            multiline
                                                            onBlur={() => handleSaveGoal(loopKPI.kpi)}
                                                        />
                                                    </Box>
                                                </Box>
                                            </ListItem>
                                            {index < group.kpis.length - 1 && <Divider />}
                                        </Box>
                                    );
                                })}
                            </List>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
} 